import { useCallback, useRef, useState } from 'react';

import {
    THEATER_BANNER_CONFIG_KEY,
    THEATER_BANNER_CONFIG_MODULE,
} from '@/constants/theater-banner';
import {
    buildBannerConfigValue,
    hydrateBannerItems,
    parseBannerConfigValue,
} from '@/pages/client-banner/utils';
import { patchConfigsKeyBanner } from '@/services/generated/config';
import {
    isServiceConfigVersionConflict,
    serviceConfigList,
    type ServiceConfigItem,
} from '@/services/service-config';

import type { BannerEditorItem, TheaterBannerConfig } from '@/types/theater-banner';

const toConfigRecord = (data: unknown): ServiceConfigItem | null => {
    if (!data || typeof data !== 'object') return null;
    return data as ServiceConfigItem;
};

export const useBannerConfig = () => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [configRecord, setConfigRecord] = useState<ServiceConfigItem | null>(null);
    const [settings, setSettings] = useState<Pick<TheaterBannerConfig, 'enabled'>>({
        enabled: false,
    });
    const [items, setItems] = useState<BannerEditorItem[]>([]);

    const versionRef = useRef<number | undefined>(undefined);

    const findConfigRecord = useCallback(async (): Promise<ServiceConfigItem | null> => {
        let page = 1;
        const size = 100;

        while (true) {
            const { data } = await serviceConfigList({ page, size });
            if (data.code !== 200) {
                throw new Error(data.msg);
            }

            const matched =
                data.data?.find(
                    (item) => item.key === THEATER_BANNER_CONFIG_KEY && item.module === THEATER_BANNER_CONFIG_MODULE,
                ) ?? null;

            if (matched) {
                return matched;
            }

            const total = data.meta?.total ?? 0;
            const fetched = page * size;
            if (fetched >= total || !(data.data?.length)) {
                return null;
            }

            page += 1;
        }
    }, []);

    const loadConfig = useCallback(async () => {
        setLoading(true);
        try {
            const record = await findConfigRecord();
            setConfigRecord(record);
            versionRef.current = record?.version;

            const parsed = parseBannerConfigValue(record?.value);
            setSettings({
                enabled: parsed.enabled,
            });
            setItems(hydrateBannerItems(parsed.items, new Map()));
        } finally {
            setLoading(false);
        }
    }, [findConfigRecord]);

    const save = useCallback(
        async (nextItems: BannerEditorItem[], nextSettings: Pick<TheaterBannerConfig, 'enabled'>) => {
            setSaving(true);
            try {
                const value = buildBannerConfigValue(nextItems, nextSettings);

                const { data } = await patchConfigsKeyBanner({
                    value,
                    version: versionRef.current ?? 0,
                } as Parameters<typeof patchConfigsKeyBanner>[0]);

                if (isServiceConfigVersionConflict(data)) {
                    return { conflict: true as const };
                }

                if (data.code !== 200) {
                    throw new Error(data.msg);
                }

                const record = toConfigRecord(data.data);
                if (record) {
                    setConfigRecord(record);
                    versionRef.current = record.version;
                } else if (data.data?.version !== undefined) {
                    versionRef.current = data.data.version;
                }

                return { conflict: false as const };
            } finally {
                setSaving(false);
            }
        },
        [],
    );

    return {
        loading,
        saving,
        configRecord,
        settings,
        setSettings,
        items,
        setItems,
        loadConfig,
        save,
    };
};
